/** Empty and error states — design spec §7 and §9. */

import type { ReactNode } from "react";
import { CloudOff, RotateCw } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

/**
 * §7 — `--sbg` panel, radius 10, padding 48/24, centred: a 32px icon at .7
 * opacity, a 16/590 title, a 14px explanation capped at 250px, and optionally
 * one compact primary action.
 *
 * §7 also demands the copy be concrete and actionable — "здесь пока пусто"
 * tells the user nothing, so callers pass a real next step.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center rounded-card border border-p-border bg-p-surface px-6 py-12 text-center",
        className,
      )}
    >
      {icon != null && (
        <span className="mb-3 flex h-8 w-8 items-center justify-center text-hint opacity-70">
          {icon}
        </span>
      )}
      <h3 className="text-section font-medium text-text">{title}</h3>
      {description != null && (
        <p className="mt-1 max-w-prose text-body text-hint">{description}</p>
      )}
      {action != null && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * §9 — the third list state. `cloud-off` in the text colour (not red: Spec 020 D2), a concrete "what failed"
 * line, and an outlined retry. When we still hold cached data the screen also
 * raises a top toast saying it may be stale; that is the caller's job.
 */
export function ErrorState({
  title = "Не удалось загрузить",
  description = "Проверь соединение и попробуй ещё раз.",
  onRetry,
  retrying,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center rounded-card border border-p-border bg-p-surface px-6 py-12 text-center",
        className,
      )}
      role="alert"
    >
      <CloudOff className="mb-3 h-8 w-8 text-p-text" aria-hidden="true" />
      <h3 className="text-section font-medium text-text">{title}</h3>
      <p className="mt-1 max-w-prose text-body text-hint">{description}</p>
      {onRetry && (
        <Button
          variant="secondary"
          size="compact"
          className="mt-4 border border-sep bg-transparent"
          onClick={onRetry}
          loading={retrying}
          loadingText="Повторяем…"
          icon={<RotateCw className="h-4 w-4" aria-hidden="true" />}
        >
          Повторить
        </Button>
      )}
    </div>
  );
}
